/**
 * 国土地理院ジオコーディングAPIサービス
 * 住所から緯度・経度を取得するためのサービス
 * 
 * APIドキュメント:
 * https://msearch.gsi.go.jp/address-search/AddressSearch
 */

const API_BASE_URL = 'https://msearch.gsi.go.jp/address-search/AddressSearch'

/**
 * 住所検索エラー
 */
class GeocodingError extends Error {
    constructor(message, code) {
        super(message)
        this.name = 'GeocodingError'
        this.code = code
    }
}

/**
 * 国土地理院のジオコーディングAPIサービス
 */
const geocodingApi = {
    /**
     * 住所から緯度・経度を取得
     * @param {string} address - 検索する住所
     * @returns {Promise<Object>} 検索結果 { lat: number, lng: number, title: string }
     * @throws {GeocodingError} API呼び出しエラーまたは結果なし
     */
    async searchAddress(address) {
        // 入力チェック
        if (!address || typeof address !== 'string' || address.trim() === '') {
            throw new GeocodingError('住所を入力してください', 'INVALID_INPUT')
        }

        try {
            // URLエンコードして住所を含むURLを構築
            const encodedAddress = encodeURIComponent(address.trim())
            const url = `${API_BASE_URL}?q=${encodedAddress}`

            console.log('[GeocodingAPI] リクエスト:', url)

            // APIリクエスト
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            })

            // HTTPステータスチェック
            if (!response.ok) {
                console.error('[GeocodingAPI] HTTPエラー:', response.status, response.statusText)
                throw new GeocodingError(
                    `APIリクエストに失敗しました (${response.status})`,
                    'HTTP_ERROR'
                )
            }

            // レスポンスをJSONとしてパース
            const data = await response.json()

            console.log('[GeocodingAPI] レスポンス:', data)

            // 結果チェック
            if (!Array.isArray(data) || data.length === 0) {
                throw new GeocodingError(
                    '指定された住所が見つかりませんでした',
                    'NO_RESULTS'
                )
            }

            // 最初の結果を使用（通常は最も関連性の高い結果）
            const firstResult = data[0]

            // データ構造の検証
            if (!firstResult.geometry ||
                !firstResult.geometry.coordinates ||
                !Array.isArray(firstResult.geometry.coordinates) ||
                firstResult.geometry.coordinates.length < 2) {
                console.error('[GeocodingAPI] 不正なレスポンス形式:', firstResult)
                throw new GeocodingError(
                    'APIレスポンスの形式が不正です',
                    'INVALID_RESPONSE'
                )
            }

            // GeoJSON形式の座標: [経度, 緯度]
            const [lng, lat] = firstResult.geometry.coordinates
            const title = firstResult.properties?.title || address

            // 座標の妥当性チェック
            if (typeof lat !== 'number' || typeof lng !== 'number' ||
                lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                console.error('[GeocodingAPI] 不正な座標:', { lat, lng })
                throw new GeocodingError(
                    '取得した座標が不正です',
                    'INVALID_COORDINATES'
                )
            }

            console.log('[GeocodingAPI] 検索成功:', { lat, lng, title })

            return {
                lat,
                lng,
                title,
                originalData: firstResult
            }

        } catch (error) {
            // GeocodingErrorはそのまま再スロー
            if (error instanceof GeocodingError) {
                throw error
            }

            // ネットワークエラーなどの場合
            console.error('[GeocodingAPI] エラー:', error)

            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                throw new GeocodingError(
                    'ネットワークエラーが発生しました。インターネット接続を確認してください',
                    'NETWORK_ERROR'
                )
            }

            // その他の予期しないエラー
            throw new GeocodingError(
                `住所検索中にエラーが発生しました: ${error.message}`,
                'UNKNOWN_ERROR'
            )
        }
    },

    /**
     * 複数の住所候補を取得（将来の拡張用）
     * @param {string} address - 検索する住所
     * @param {number} limit - 取得する最大件数（デフォルト: 5）
     * @returns {Promise<Array>} 検索結果の配列
     */
    async searchAddressMultiple(address, limit = 5) {
        if (!address || typeof address !== 'string' || address.trim() === '') {
            throw new GeocodingError('住所を入力してください', 'INVALID_INPUT')
        }

        try {
            const encodedAddress = encodeURIComponent(address.trim())
            const url = `${API_BASE_URL}?q=${encodedAddress}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            })

            if (!response.ok) {
                throw new GeocodingError(
                    `APIリクエストに失敗しました (${response.status})`,
                    'HTTP_ERROR'
                )
            }

            const data = await response.json()

            if (!Array.isArray(data) || data.length === 0) {
                return []
            }

            // 指定された件数まで結果を返す
            return data.slice(0, limit).map(result => {
                const [lng, lat] = result.geometry.coordinates
                return {
                    lat,
                    lng,
                    title: result.properties?.title || address,
                    originalData: result
                }
            })

        } catch (error) {
            if (error instanceof GeocodingError) {
                throw error
            }

            console.error('[GeocodingAPI] エラー:', error)
            throw new GeocodingError(
                `住所検索中にエラーが発生しました: ${error.message}`,
                'UNKNOWN_ERROR'
            )
        }
    }
}

export default geocodingApi
export { GeocodingError }
